import React, { useState } from 'react';
import { Box, Typography, Button, Chip, Alert, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Drawer, Divider } from '@mui/material';
import { C } from '../../../theme';

function formatTime(s) {
  if (s <= 0) return 'Expired';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ============================================================
// ChatHeader
// Owns: menu UI only.
// No feedback state, no FeedbackModal, no API calls.
// All actions delegate to trigger* props from useFeedbackTrigger.
// ============================================================
function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.2 }}>
      <Typography sx={{ fontSize: 13, color: '#888' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, color: '#EFEFEF', textAlign: 'right', maxWidth: '60%' }}>{value}</Typography>
    </Box>
  );
}

export default function ChatHeader({
  fromGame, navigate,
  otherUser, fsmState, secondsRemaining,
  serverState, error, setError, isUnlocked,
  matchId,
  otherCheckin,
  onTriggerWeMet,
  onTriggerUnmatch,
  onTriggerDeleteChat,
  onTriggerReport,
}) {
  const [menuAnchor,       setMenuAnchor]       = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  const openMenu  = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = ()  => setMenuAnchor(null);

  const handleWeMet = () => {
    closeMenu();
    onTriggerWeMet(matchId, serverState?.otherUserId);
  };

  const handleUnmatch = () => {
    closeMenu();
    if (!window.confirm('Are you sure you want to unmatch?')) return;
    onTriggerUnmatch(matchId, serverState?.otherUserId);
  };

  const handleDeleteChat = () => {
    closeMenu();
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteChat = () => {
    setDeleteConfirmOpen(false);
    onTriggerDeleteChat(matchId, serverState?.otherUserId);
  };

  const handleReport = () => {
    closeMenu();
    onTriggerReport(matchId, serverState?.otherUserId);
  };

  return (
    <>
      {/* GAME BANNER */}
      {fromGame && (
        <Box sx={{
          background: C.bgCard,
          borderBottom: `1px solid ${C.yellow}`,
          padding: '8px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <Typography sx={{ fontSize: '12px', color: C.yellow }}>🎮 Game is live</Typography>
          <Button size="small" sx={{ fontSize: '11px', color: C.yellow, padding: '2px 8px' }}
            onClick={() => navigate('/game')}>Return to Play →</Button>
        </Box>
      )}

      {/* HEADER BAR */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        p: 1.5, borderBottom: `1px solid ${C.border}`, flexShrink: 0,
        bgcolor: C.bgCard, position: 'sticky', top: 0, zIndex: 10
      }}>
        <Button size="small" onClick={() => navigate('/chats')} sx={{ color: C.yellow }}>← Back</Button>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            onClick={() => setProfileDrawerOpen(true)}
            sx={{ fontWeight: 'bold', lineHeight: 1.2, color: C.textPrimary, cursor: 'pointer',
                  textDecoration: 'underline', textDecorationColor: 'rgba(239,239,239,0.3)',
                  textUnderlineOffset: '3px' }}
          >
            {otherUser?.alias || '...'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={`⏱ ${formatTime(secondsRemaining)}`}
            size="small"
            sx={{
              bgcolor: secondsRemaining > 3600 ? C.teal : C.orange,
              color: '#EFEFEF', fontWeight: 'bold', fontSize: '0.7rem'
            }}
          />
          {serverState?.otherUserId && (
            <Button
              size="small"
              onClick={openMenu}
              sx={{
                minWidth: 0, px: 0.5, color: C.textMuted, fontSize: '18px', lineHeight: 1,
                '&:hover': { color: C.textPrimary, background: 'none' }
              }}
            >
              ⋮
            </Button>
          )}
        </Box>
      </Box>

      {/* HAMBURGER MENU */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            bgcolor: C.bgElevated,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            minWidth: 160,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleWeMet}
          sx={{ height: 44, display: 'flex', alignItems: 'center', fontSize: 14,
                color: C.textPrimary, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
          We Met
        </MenuItem>
        <MenuItem onClick={handleUnmatch}
          sx={{ height: 44, display: 'flex', alignItems: 'center', fontSize: 14,
                color: C.textPrimary, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
          Unmatch
        </MenuItem>
        <MenuItem onClick={handleDeleteChat}
          sx={{ height: 44, display: 'flex', alignItems: 'center', fontSize: 14,
                color: C.textPrimary, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
          Delete Chat
        </MenuItem>
        <MenuItem onClick={handleReport}
          sx={{ height: 44, display: 'flex', alignItems: 'center', fontSize: 14,
                color: '#E53935', borderTop: `1px solid ${C.border}`,
                '&:hover': { bgcolor: 'rgba(229,57,53,0.08)' } }}>
          Report
        </MenuItem>
      </Menu>

      {/* DELETE CHAT CONFIRM DIALOG */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { bgcolor: '#1E1E1E', border: '1px solid #333', borderRadius: 2, minWidth: 280 } }}
      >
        <DialogTitle sx={{ color: '#EFEFEF', fontSize: 16, pb: 1 }}>Delete Chat</DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#AAAAAA', fontSize: 14 }}>
            This will unmatch and delete. Do you confirm?
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}
            sx={{ color: '#AAAAAA', fontSize: 13 }}>Cancel</Button>
          <Button onClick={confirmDeleteChat}
            sx={{ color: '#E53935', fontSize: 13, fontWeight: 'bold' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* PROFILE DRAWER */}
      <Drawer
        anchor="bottom"
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        PaperProps={{ sx: { bgcolor: '#1A1A1A', borderRadius: '16px 16px 0 0', px: 2.5, pt: 2.5, pb: 4, maxHeight: '70vh' } }}
      >
        <Box sx={{ width: 40, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, mx: 'auto', mb: 2.5 }} />
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#EFEFEF', mb: 0.5 }}>
          {otherUser?.alias || '...'}
        </Typography>
        {otherUser?.age_group && (
          <Typography sx={{ fontSize: 13, color: '#888', mb: 2 }}>{otherUser.age_group}</Typography>
        )}
        {otherCheckin ? (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />
            <Typography sx={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
              Session identifiers
            </Typography>
            {otherCheckin.location_area && (
              <Row label="Location" value={otherCheckin.location_area} />
            )}
            {(otherCheckin.clothing_primary || otherCheckin.clothing_secondary) && (
              <Row label="Wearing" value={[otherCheckin.clothing_primary, otherCheckin.clothing_secondary].filter(Boolean).join(' + ')} />
            )}
            {otherCheckin.accessories && (
              <Row label="Accessories" value={otherCheckin.accessories} />
            )}
            {otherCheckin.group_status && (
              <Row label="Group" value={otherCheckin.group_status} />
            )}
            {!!otherCheckin.wearing_glasses_now && (
              <Row label="Glasses" value="Yes" />
            )}
          </>
        ) : (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />
            <Typography sx={{ fontSize: 13, color: '#666' }}>No session info available.</Typography>
          </>
        )}
      </Drawer>

      {/* ERROR */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mt: 1, flexShrink: 0 }}>{error}</Alert>
      )}

      {/* UNLOCK NOTICE */}
      {!isUnlocked && (
        <Alert severity="info" sx={{ mx: 2, mt: 1, flexShrink: 0 }} icon={false}>
          Answer 2 questions to unlock: find each other &amp; plan a meet
        </Alert>
      )}
    </>
  );
}
